import {
  Divider,
  Drawer as MuiDrawer,
  List,
  Stack,
  Typography
} from '@mui/material';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import KeyboardDoubleArrowLeftSharpIcon from '@mui/icons-material/KeyboardDoubleArrowLeftSharp';
import KeyboardDoubleArrowRightSharpIcon from '@mui/icons-material/KeyboardDoubleArrowRightSharp';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import IconButton from '@mui/material/IconButton';
import './app-drawer.scss';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import {
  getLegalDocument,
  getUserGuide
} from 'src/pages/analytics/landing/store/landing-slice';
import Tooltip from '@mui/material/Tooltip';
// import { DRAWER_WIDTH } from 'src/app-constants';
import { BUILD_VERSION } from 'src/constants';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ListItem from './list-item';
import { setIsDrawerOpen } from './app-drawer-slice';

const openedMixin = (theme: Theme): CSSObject => ({
  // TODO: change this widht value to DRAWER_WIDTH after resolving the path issue
  width: 320,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  top: `calc(${theme.spacing(3)} + 1px)`
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  },
  top: `calc(${theme.spacing(3)} + 1px)`,
  '@media (min-width: 600px)': {
    width: `calc(${theme.spacing(7)} + 1px)`
  }
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  backgroundColor: '#FCFCFC',
  '& .MuiDrawer-paper': {
    backgroundColor: '#FCFCFC'
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': {
      overflowY: 'hidden',
      height: `calc(100vh - ${theme.spacing(3)} + 1px)`,
      ...openedMixin(theme)
    }
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': {
      overflowY: 'hidden',
      height: `calc(100vh - ${theme.spacing(3)} + 1px)`,
      ...closedMixin(theme)
    }
  })
}));

export default function AppDrawer() {
  const { navOptions, isDrawerOpen } = useAppSelector(
    (state) => state.appDrawer
  );
  const appDispatch = useAppDispatch();

  const [activeIndex, setActiveIndex] = useState(0);

  const location = useLocation(); // React Router's location hook

  // setting the active index
  const getActiveItem = (index) => {
    setActiveIndex(index);
  };

  // Function to get the current nav index based on URL
  const getCurrentNavIndex = () => {
    // getting the current active page
    const currUrl = location.pathname.split('/').pop();
    // getting the index of parent object as per the current url
    const currIndex = navOptions.findIndex((navOption) =>
      navOption.children.some(
        (child) => child.parent.route_url.split('/').pop() === currUrl
      )
    );
    console.log('Index of the parent object in navOptions:', currIndex);
    return currIndex;
  };

  // useEffect for highlighting respectivenavbar options as per the current page
  useEffect(() => {
    // Trigger function whenever the path changes
    const currActiveIndex = getCurrentNavIndex();
    setActiveIndex(currActiveIndex > 0 ? currActiveIndex : 0);
  }, [location.pathname]);

  /**
   * handler for download user guide
   */
  const handleDownload = () => {
    appDispatch(getUserGuide())
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.error('Failed to download summary', error);
      });
  };

  /**
   * handler for download legal document
   */
  const handleLegalDocumentDownload = () => {
    appDispatch(getLegalDocument())
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.error('Failed to download document', error);
      });
  };

  return (
    <Drawer className='app-drawer' variant='permanent' open={isDrawerOpen}>
      <List sx={{ width: '100%', overflowY: 'auto', marginBottom: 4 }}>
        {navOptions.map((navOption, index) => (
          <div
            key={index}
            className='list-item-div'>
            <ListItem
              itemIndex={index}
              navOption={navOption}
              open={isDrawerOpen}
              isActive={index === activeIndex}
              getActiveItem={getActiveItem}
            />
          </div>
        ))}
      </List>
      <div
        className={`drawer-state-toggle2 ${
          isDrawerOpen ? 'expand' : 'collapse'
        }`}
        style={{bottom: '76px'}}
      >
        {isDrawerOpen === true && (
          <Stack direction='row'>
            <Typography
              variant='body1'
              align='center'
              sx={{ marginLeft: '10px' }}
            >
              Legal Compliance
            </Typography>
          </Stack>
        )}
        <IconButton onClick={handleLegalDocumentDownload}>
          <img
            src='/static/images/legal_document.png'
            style={{height: '16px', width: '16px', marginRight: '4px'}}
            alt='legal document'
          />
        </IconButton>
      </div>
      <div
        className={`drawer-state-toggle2 ${
          isDrawerOpen ? 'expand' : 'collapse'
        }`}
      >
        {isDrawerOpen === true && (
          <Stack direction='row'>
            <Typography
              variant='body1'
              align='center'
              sx={{ marginLeft: '10px' }}
            >
              Tool Guide
            </Typography>
          </Stack>
        )}
        <IconButton onClick={handleDownload}>
          <QuestionMarkIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </div>
      <div
        className={`drawer-state-toggle ${
          isDrawerOpen ? 'expand' : 'collapse'
        }`}
      >
        {isDrawerOpen === true && (
          <Stack direction='row'>
            <Typography
              variant='body1'
              align='center'
              sx={{ marginLeft: '10px' }}
            >
              Build version-
            </Typography>
            <Typography
              variant='body1'
              align='center'
              sx={{ fontWeight: 'bold' }}
            >
              {BUILD_VERSION}
            </Typography>
          </Stack>
        )}
        <IconButton
          aria-label='delete'
          onClick={() => appDispatch(setIsDrawerOpen(!isDrawerOpen))}
        >
          {isDrawerOpen ? (
            <KeyboardDoubleArrowLeftSharpIcon />
          ) : (
            <KeyboardDoubleArrowRightSharpIcon />
          )}
        </IconButton>
      </div>
    </Drawer>
  );
}
